import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
  ReportFieldsStructure,
  ReportValues,
  ShowMinimizePopUp,
} from '../type/modal-for-report-type';
import {
  EOSP_REPORT_FIELDS,
  NOON_REPORT_FIELDS,
  SOSP_REPORT_FIELDS,
} from './report-fields';
import { WeatherRoutingModel } from '../../../src/models/weather-routing-reports-model';
import '../../../local_modules/voyage-button/src/voyage-button';
import './modal-for-reports';
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model';
import { SHOW_WNI_REPORT_POPUP } from '../../../src/models/weather-routing-reports-model-impl';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';
import { FeatureFlagModel, FeatureFlagsEvents } from '../../../src/models/feature-flag-model';
import { VoyageToastManager } from '../../voy-toast/src/voy-toast-manager';

@customElement('report-buttons')
export class ModalForReports extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Boolean }) canCreateNoonReport = false;
  @property({ type: Array }) reportFields: ReportFieldsStructure = {};
  @property({ type: Object }) fieldValues: ReportValues = {} as ReportValues;
  @property({ type: String }) activeTab = '';
  @property({ type: Boolean }) private _isLoading = true;
  @property({ type: Object }) _fieldValues: { [key: string]: any } = {};
  @property({ attribute: false }) currentPage = 1;
  @property({ attribute: false }) private _paginationIds: number[] = [];
  @property({ attribute: false }) errorOrEmpty = false;
  @property({ attribute: true }) showMinimizePopUp: ShowMinimizePopUp = {
    minimizedPopUp: true,
    reportType: '',
    scheduleId: '',
    reportId: '',
  };
  @property({ attribute: true }) report_type = '';
  @property({ type: Number }) report_id = 0;
  @property({ attribute: true }) openedFromNotification = false;

  private _currentScheduleId = '';

  constructor() {
    super();
    this._showPopupFromNotification = this._showPopupFromNotification.bind(this);
    this._onPlanChanged = this._onPlanChanged.bind(this);
    this._onFeatureFlagsUpdated = this._onFeatureFlagsUpdated.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this._setupEventListeners();
    FeatureFlagModel?.addEventListener('featureFlagsUpdated', this._onFeatureFlagsUpdated);
    window.addEventListener(FeatureFlagsEvents.featureBasedFlagsInitialized, (e: Event) => {
      const custom = (e as CustomEvent).detail;
      if (custom.weatherRoutingEnabled) {
          const readyModel = WeatherRoutingModel;
          readyModel?.addEventListener(SHOW_WNI_REPORT_POPUP, this._showPopupFromNotification);
        }
      });
    this._onPlanChanged();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupEventListeners();
    FeatureFlagModel?.removeEventListener('featureFlagsUpdated', this._onFeatureFlagsUpdated);
  }

  private _setupEventListeners() {
    WeatherRoutingModel?.addEventListener(SHOW_WNI_REPORT_POPUP, this._showPopupFromNotification);
    VesselPlanningModel?.addEventListener(SuggestedRouteEvents.PlanChanged, this._onPlanChanged);
  }

  private _cleanupEventListeners() {
    WeatherRoutingModel?.removeEventListener(SHOW_WNI_REPORT_POPUP, this._showPopupFromNotification);
    VesselPlanningModel?.removeEventListener(SuggestedRouteEvents.PlanChanged, this._onPlanChanged);
  }

  private _onFeatureFlagsUpdated() {
    // Re-setup listeners in case models were just initialized
    this._setupEventListeners();
  }

  private async _onPlanChanged() {
    const plan = await VesselPlanningModel?.getExecutingPlan();
    this._currentScheduleId = plan?.scheduleId || '';
  }

  private async createArrivalReport(
    scheduleId: string,
  ) {
    this.errorOrEmpty = false;
    this._isLoading = true;
    this.fieldValues = {} as ReportValues;

    try {
      const response = await WeatherRoutingModel?.arrivalReportSend(scheduleId);
      if (response?.isSuccess) {
        const report = await WeatherRoutingModel?.eospReportFetch(scheduleId);
        this.fieldValues = report || ({} as ReportValues);
      } else {
        this.errorOrEmpty = true;
      }
    } catch (err) {
      console.error('Error creating Arrival report:', err);
      this.errorOrEmpty = true;
    } finally {
      this._isLoading = false;
      this.requestUpdate();
    }
  }

  private async createNoonReport() {
    this.errorOrEmpty = false;
    this._isLoading = true;
    this.fieldValues = {} as ReportValues;
    let response;

    try {
      const res = await WeatherRoutingModel?.noonReportSend(this._currentScheduleId);
      if (!res?.isSuccess) {
        VoyageToastManager.createToast({
            message: 'Noon Report creation failed: ' + res?.response?.data,
            type: 'error'
        });
      }
      
      response = res?.isSuccess ? await WeatherRoutingModel?.noonReportFetch(this._currentScheduleId, res?.id) : undefined;

      this.fieldValues = response as ReportValues || ({} as ReportValues);
      this.errorOrEmpty = !response;

      const reports = await WeatherRoutingModel?.reportIdPaginationFetch(this._currentScheduleId);
      this._paginationIds = reports?.ids;
      this.canCreateNoonReport = reports?.isSubmitted;
      this.currentPage = 1;
    } catch (err) {
      console.error('Error creating Noon report:', err);
      this.errorOrEmpty = true;
    } finally {
      this._isLoading = false;
      this.activeTab = 'noon';
      this.requestUpdate();
    }
  }

  private async fetchReport(
    scheduleId: string,
    reportType: 'Arrival' | 'Departure'
  ): Promise<void> {
    this.errorOrEmpty = false;
    this._isLoading = true;
    this.fieldValues = {} as ReportValues;
  
    try {
      // Determine appropriate functions based on the report type
      const reportFetchFn =
        reportType === 'Arrival'
          ? WeatherRoutingModel?.eospReportFetch
          : WeatherRoutingModel?.sospReportFetch;
  
      const reportSendFn =
        reportType === 'Arrival'
          ? WeatherRoutingModel?.arrivalReportSend
          : WeatherRoutingModel?.departureReportSend;
  
      // Check if reports already exist
      const reports = await WeatherRoutingModel!.checkIfReportExists(scheduleId, reportType);
      let response: ReportValues | undefined;
  
      if (reports?.ids?.length) {
        // Fetch existing reports
        response = await reportFetchFn?.(scheduleId);
      } else if (this.openedFromNotification) {
        // Send and then fetch reports if they don't exist
        const sendResponse = await reportSendFn?.(scheduleId);
        if (sendResponse?.isSuccess) {
          response = await reportFetchFn?.(scheduleId);
        }
      }
  
      // Set the field values or mark as empty/error
      this.fieldValues = response || ({} as ReportValues);
      this.errorOrEmpty = !response;
    } catch (err) {
      console.error(`Error fetching ${reportType} report:`, err);
      this.errorOrEmpty = true;
    } finally {
      this.openedFromNotification = false;
      this._isLoading = false;
      this.requestUpdate();
    }
  }

  private async handleGetPagination(id?: number) {

    try {
      this._isLoading = true;
      const reports = await WeatherRoutingModel?.reportIdPaginationFetch(
        this._currentScheduleId
      );

      this.canCreateNoonReport = reports?.isSubmitted;

      if(reports?.ids?.length) {
        this._paginationIds = reports?.ids;
        const reportId = id || reports?.ids?.[0];
        if (reports?.isSubmitted && this.openedFromNotification) {
          await this.fetchNoonReport(undefined);
          const reports = await WeatherRoutingModel?.reportIdPaginationFetch(this._currentScheduleId);
          this._paginationIds = reports?.ids;
        } else {
          await this.fetchNoonReport(reportId);
        }
      } else if(this.openedFromNotification) {
          await this.fetchNoonReport(undefined);
          const reports = await WeatherRoutingModel?.reportIdPaginationFetch(this._currentScheduleId);
          this._paginationIds = reports?.ids;
        }
    } catch (err) {
      console.error('Error fetching pagination:', err);
      this.errorOrEmpty = true;
    }
  }

  private async fetchNoonReport(id?: number) {
    try {
      this.errorOrEmpty = false;
      this._isLoading = true;
      this.fieldValues = {} as ReportValues;
      let response;

      if(id) {
          response = await WeatherRoutingModel?.noonReportFetch(this._currentScheduleId, id)
      } else if (this.openedFromNotification) {
        const res = await WeatherRoutingModel?.noonReportSend(this._currentScheduleId);
        response = res?.isSuccess ? await WeatherRoutingModel?.noonReportFetch(this._currentScheduleId, res?.id) : undefined;
      }

      this.fieldValues = response as ReportValues || ({} as ReportValues);
      this.errorOrEmpty = !response;
    } catch (err) {
      console.error('Error fetching Noon report:', err);
      this.errorOrEmpty = true;
    } finally {
      this._isLoading = false;
      this.openedFromNotification = false;
      this.activeTab = 'noon';
      this.requestUpdate();
    }
  }

  private _showPopupFromNotification(e: Event) {
    const { minimizedPopUp, reportType, reportId, scheduleId, fromNotification } = (e as CustomEvent).detail;
    this.showMinimizePopUp = { minimizedPopUp, reportType, reportId, scheduleId };
    this.report_type = reportType;
    this.report_id = reportId;
    this.openedFromNotification = fromNotification;
    this.showData();
  }

  private async showData() {
    this.open = true;
    const tabMap: Record<string, string> = {
      Departure: 'sosp',
      Arrival: 'eosp',
      Noon: 'noon',
    };

    const activeTab = tabMap[this.report_type];
    if (activeTab) {
      this.activeTab = activeTab;
      await this._handleApiCall(activeTab);
    }
  }


  private async _handleApiCall(tabId: string) {
    this._isLoading = true;
    this._fieldValues = {};
    this.fieldValues = {} as ReportValues;
    this.reportFields = {} as ReportFieldsStructure;
    const fieldMap: { [key: string]: any } = {
      sosp: SOSP_REPORT_FIELDS,
      eosp: EOSP_REPORT_FIELDS,
      noon: NOON_REPORT_FIELDS,
    };

    const fetchFnMap : { [key: string]: any } = {
      sosp: () => this.fetchReport(this._currentScheduleId, 'Departure'),
      eosp: () => this.fetchReport(this._currentScheduleId, 'Arrival'),
      noon: () => this.handleGetPagination(),
    };

    this.reportFields = fieldMap[tabId] || {};
    await fetchFnMap[tabId]?.();
  }

  static get styles() {
    return [
        css`
            voyage-button {
                fill: var(--neutrals-grey-04, grey);
                font-family: var(--component-heading-ff, 'serif');
                font-size: 14px;
                line-height: 20px;
                font-weight: normal;
                height: 32px;
            }

            voyage-button > span {
                color: var(--neutrals-grey-03, black);
            }

            voyage-button > svg {
                height: 14px;
            }
            .button-row {
                position: fixed;
                top: 55px;
                right: 5px;
                left: 435px;
                display: flex;
                align-items: flex-start;
                pointer-events: none;
            }

            .button-row voyage-button {
                margin-left: 10px;
                pointer-events: auto;
            }
            .main {
                width: 100%;
                flex-grow: 1;
                z-index: 99999;
            }
        `,
    ]
  }

  render() {
    return html`
      <div class="main">
        <modal-for-reports
          .showMinimizePopUp=${this.showMinimizePopUp}
          @active-tab=${(e: CustomEvent)=>this._handleApiCall(e.detail.tabId)}
          @report-modal-close=${() => (this.open = false)}
          .open=${this.open}
          .fields=${this.reportFields}
          @noon-current-page=${(e: CustomEvent) => this.fetchNoonReport(e.detail.currentPage)}
          .fieldValues=${this.fieldValues}
          .activeTab=${this.activeTab}
          ._isLoading=${this._isLoading}
          .paginationIds=${this._paginationIds}
          .currentPage=${this.currentPage}
          .errorOrEmpty=${this.errorOrEmpty}
          .canCreateNoonReport=${this.canCreateNoonReport}
          @create-eosp-report=${() => this.createArrivalReport(this._currentScheduleId)}
          @create-noon-report=${() => this.createNoonReport()}
        ></modal-for-reports>
      </div>
    `;
  }
}